import React, { useState, useRef } from "react";
import { PhotoIcon, TrashIcon } from "@heroicons/react/24/outline";
import { Button } from "./ui/Button";
import { apiService } from "../services/api";
import { processPlayerPhoto } from "../utils/imageProcessor";
import toast from "react-hot-toast";

const PlayerPhotoUpload = ({ player, onPhotoUpdated }) => {
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    // Validate file size (10MB limit for original, will be compressed)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image must be smaller than 10MB");
      return;
    }

    try {
      // Process image client-side (resize and compress)
      setProcessing(true);
      const processedFile = await processPlayerPhoto(file);
      setProcessing(false);

      // Upload processed image
      setUploading(true);
      const result = await apiService.uploadPlayerPhoto(player.id, processedFile);
      toast.success("Photo uploaded successfully!");
      onPhotoUpdated && onPhotoUpdated(result.player);
    } catch (error) {
      console.error("Error uploading photo:", error);
      toast.error("Failed to upload photo");
    } finally {
      setProcessing(false);
      setUploading(false);
      // Clear the input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleDeletePhoto = async () => {
    if (!player.photo_url) return;

    setDeleting(true);
    try {
      const result = await apiService.deletePlayerPhoto(player.id);
      toast.success("Photo deleted successfully!");
      onPhotoUpdated && onPhotoUpdated(result.player);
    } catch (error) {
      console.error("Error deleting photo:", error);
      toast.error("Failed to delete photo");
    } finally {
      setDeleting(false);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="flex flex-col items-center space-y-3">
      {/* Photo Display */}
      <div className="relative w-24 h-24 rounded-full overflow-hidden bg-gray-200 border-2 border-gray-300">
        {player.photo_url ? (
          <img
            src={player.photo_url}
            alt={`${player.first_name} ${player.last_name}`}
            className="w-full h-full object-cover"
            onError={(e) => {
              // Fallback if image fails to load
              e.target.style.display = "none";
              e.target.nextSibling.style.display = "flex";
            }}
          />
        ) : null}

        {/* Fallback avatar */}
        <div
          className={`absolute inset-0 flex items-center justify-center ${
            player.photo_url ? "hidden" : "flex"
          }`}
        >
          <PhotoIcon className="w-8 h-8 text-gray-400" />
        </div>

        {/* Loading overlay */}
        {(processing || uploading || deleting) && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center flex-col">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
            {processing && (
              <span className="text-white text-xs mt-1">Processing...</span>
            )}
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex space-x-2">
        <Button
          size="sm"
          variant="outline"
          onClick={triggerFileInput}
          disabled={processing || uploading || deleting}
        >
          <PhotoIcon className="w-4 h-4 mr-1" />
          {player.photo_url ? "Change" : "Upload"}
        </Button>

        {player.photo_url && (
          <Button
            size="sm"
            variant="outline"
            onClick={handleDeletePhoto}
            disabled={processing || uploading || deleting}
            className="text-red-600 hover:text-red-700 border-red-300 hover:border-red-400"
          >
            <TrashIcon className="w-4 h-4 mr-1" />
            Delete
          </Button>
        )}
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Helper text */}
      <p className="text-xs text-gray-500 text-center">
        JPG, PNG, or GIF • Max 5MB
      </p>
    </div>
  );
};

export default PlayerPhotoUpload;
